import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Gift,
  Award,
  Users,
  Copy,
  Check,
  ChevronRight,
  Share2,
  QrCode,
  Star,
  TrendingUp,
  Sparkles,
  Tag,
  Percent,
  Clock,
  Crown,
  Download,
  MessageCircle,
  Mail,
  Facebook,
  Twitter,
  Linkedin,
  Send,
  DollarSign,
  BadgeCheck,
  ArrowUpRight,
  Wallet
} from "lucide-react";

export default function RewardsCenterPage() {
  const { user } = useAuth();
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [influencerStatus, setInfluencerStatus] = useState(null);
  const [activePromotions, setActivePromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState({ method: "etransfer", etransfer_email: "", paypal_email: "" });

  useEffect(() => {
    fetchRewardsData();
  }, []);

  const fetchRewardsData = async () => {
    try {
      const [loyaltyRes, referralRes, promosRes, influencerRes] = await Promise.all([
        apiClient.get("/payments/loyalty/status").catch(() => ({ data: null })),
        apiClient.get("/referral/my-code").catch(() => ({ data: null })),
        apiClient.get("/promotions/active").catch(() => ({ data: { promotions: [] } })),
        apiClient.get("/influencer/status").catch(() => ({ data: null }))
      ]);

      setLoyaltyStatus(loyaltyRes.data);
      setReferralInfo(referralRes.data);
      setActivePromotions(promosRes.data?.promotions || []);
      setInfluencerStatus(influencerRes.data);
      
      // Set payout method if exists
      if (influencerRes.data?.payout_method) {
        setPayoutMethod(influencerRes.data.payout_method);
      }
    } catch (error) {
      console.error("Failed to fetch rewards data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferral = async () => {
    if (referralInfo?.referral_url) {
      await navigator.clipboard.writeText(referralInfo.referral_url);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: MessageCircle,
      color: "bg-green-600",
      action: () => {
        const text = `Join me on Viraly! ${referralInfo?.share_message || "Get free credits!"}\n\n${referralInfo?.referral_url}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-purple-600",
      action: () => {
        const subject = "Join Viraly - AI Social Media Automation";
        const body = `${referralInfo?.share_message}\n\n${referralInfo?.referral_url}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
      }
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-blue-600",
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralInfo?.referral_url)}`, '_blank');
      }
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "bg-black",
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(referralInfo?.share_message)}&url=${encodeURIComponent(referralInfo?.referral_url)}`, '_blank');
      }
    }
  ];

  const handleSavePayoutMethod = async () => {
    try {
      await apiClient.put("/influencer/payout-method", payoutMethod);
      toast.success("Payout method saved!");
      setShowPayoutModal(false);
    } catch (err) {
      toast.error("Failed to save payout method");
    }
  };

  const handleRequestPayout = async () => {
    try {
      const res = await apiClient.post("/influencer/request-payout");
      toast.success(`Payout request submitted for $${res.data.amount}!`);
      fetchRewardsData(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request payout");
    }
  };

  const getTierColor = (tierName) => {
    const colors = {
      bronze: "from-amber-700 to-amber-500",
      silver: "from-gray-400 to-gray-300",
      gold: "from-yellow-500 to-yellow-300",
      platinum: "from-purple-600 to-purple-400",
      diamond: "from-cyan-400 to-blue-500"
    };
    return colors[tierName] || "from-gray-500 to-gray-400";
  };

  const getTierIcon = (tierName) => {
    const icons = {
      bronze: "🥉",
      silver: "🥈",
      gold: "🥇",
      platinum: "💎",
      diamond: "👑"
    };
    return icons[tierName] || "⭐";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Gift className="w-8 h-8 text-viraly-orange" />
              Rewards Center
            </h1>
            <p className="text-viraly-muted mt-1">
              Earn discounts, refer friends, and unlock exclusive benefits
            </p>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-base p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
              <Crown className="w-6 h-6 text-viraly-orange" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Your Tier</p>
              <p className="text-lg font-bold">
                {loyaltyStatus?.current_tier ? loyaltyStatus.current_tier.charAt(0).toUpperCase() + loyaltyStatus.current_tier.slice(1) : "Not Yet Earned"}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-base p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Percent className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Your Discount</p>
              <p className="text-lg font-bold">{loyaltyStatus?.discount_percent || 0}% off</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-base p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">12-Month Spend</p>
              <p className="text-lg font-bold">${loyaltyStatus?.spend_12_months?.toFixed(2) || "0.00"}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-base p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Referrals</p>
              <p className="text-lg font-bold">{referralInfo?.total_referrals || 0}</p>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Loyalty Tiers */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card-base p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-viraly-orange" />
                Loyalty Tiers
              </h2>
              <span className="text-xs text-viraly-muted">Rolling 12-month spend</span>
            </div>

            {loyaltyStatus?.message && (
              <div className="mb-4 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
                <p className="text-sm text-viraly-orange">{loyaltyStatus.message}</p>
              </div>
            )}

            {/* Progress to next tier */}
            {loyaltyStatus?.next_tier && loyaltyStatus?.spend_to_next_tier > 0 && (
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-viraly-muted">Progress to next tier</span>
                  <span className="text-viraly-orange font-medium">
                    ${loyaltyStatus.spend_to_next_tier.toFixed(2)} more needed
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.min(100, ((loyaltyStatus.spend_12_months / loyaltyStatus.next_tier.min_spend) * 100))}%`
                    }}
                    className="h-full bg-viraly-gradient"
                  />
                </div>
              </div>
            )}

            {/* Tier Cards */}
            <div className="space-y-3">
              {loyaltyStatus?.tiers?.map((tier, index) => (
                <div
                  key={tier.name}
                  className={`p-4 rounded-xl border transition-all ${
                    tier.current
                      ? "border-viraly-orange bg-viraly-orange/10"
                      : tier.reached
                      ? "border-green-500/30 bg-green-500/5"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTierColor(tier.name)} flex items-center justify-center text-lg`}>
                        {getTierIcon(tier.name)}
                      </div>
                      <div>
                        <p className="font-medium capitalize flex items-center gap-2">
                          {tier.name}
                          {tier.current && (
                            <span className="text-xs bg-viraly-orange px-2 py-0.5 rounded-full">Current</span>
                          )}
                        </p>
                        <p className="text-xs text-viraly-muted">
                          Spend ${tier.min_spend}+ in 12 months
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-400">{tier.discount_percent}%</p>
                      <p className="text-xs text-viraly-muted">off all purchases</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Downgrade Warning */}
            {loyaltyStatus?.downgrade_warning?.warning && (
              <div className="mt-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-400">Tier Expiring Soon</p>
                    <p className="text-xs text-viraly-muted mt-1">
                      Spend ${loyaltyStatus.downgrade_warning.spend_needed?.toFixed(2)} more in the next {loyaltyStatus.downgrade_warning.days_until} days to maintain your {loyaltyStatus.downgrade_warning.current_tier} status.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Referral Program */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card-base p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Share2 className="w-5 h-5 text-viraly-orange" />
                Refer & Earn
              </h2>
              <button
                onClick={() => setShowQR(!showQR)}
                className="text-sm text-viraly-orange hover:underline flex items-center gap-1"
              >
                <QrCode className="w-4 h-4" />
                {showQR ? "Hide" : "Show"} QR Code
              </button>
            </div>

            {/* Rewards Summary */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <p className="text-xs text-green-400 mb-1">You Get</p>
                <p className="font-medium text-sm">{referralInfo?.rewards?.you_get || "Free credits per referral"}</p>
              </div>
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-400 mb-1">Your Friend Gets</p>
                <p className="font-medium text-sm">{referralInfo?.rewards?.friend_gets || "Discount on first purchase"}</p>
              </div>
            </div>

            {/* QR Code */}
            {showQR && referralInfo?.qr_code_url && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6 flex justify-center"
              >
                <div className="p-4 bg-white rounded-xl">
                  <img
                    src={referralInfo.qr_code_url}
                    alt="Referral QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </motion.div>
            )}

            {/* Referral Code */}
            <div className="mb-4">
              <label className="text-sm text-viraly-muted mb-2 block">Your Referral Code</label>
              <div className="flex gap-2">
                <div className="flex-1 px-4 py-3 rounded-xl bg-viraly-dark border border-white/10 font-mono text-lg">
                  {referralInfo?.referral_code || user?.referral_code || "Loading..."}
                </div>
                <Button onClick={handleCopyReferral} className="btn-primary px-4">
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            {/* Referral Link */}
            <div className="mb-6">
              <label className="text-sm text-viraly-muted mb-2 block">Your Referral Link</label>
              <div className="flex gap-2">
                <Input
                  value={referralInfo?.referral_url || ""}
                  readOnly
                  className="input-base text-sm"
                />
                <Button onClick={handleCopyReferral} className="btn-secondary px-4">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Share Options */}
            <div>
              <label className="text-sm text-viraly-muted mb-3 block">Share via</label>
              <div className="grid grid-cols-4 gap-3">
                {shareOptions.map((option) => (
                  <button
                    key={option.name}
                    onClick={option.action}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl ${option.color} hover:opacity-90 transition-all`}
                  >
                    <option.icon className="w-5 h-5 text-white" />
                    <span className="text-xs text-white">{option.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between text-sm">
                <span className="text-viraly-muted">Total Successful Referrals</span>
                <span className="font-bold text-viraly-orange">{referralInfo?.total_referrals || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Active Promotions */}
        {activePromotions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-base p-6"
          >
            <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
              <Tag className="w-5 h-5 text-viraly-orange" />
              Active Promotions
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activePromotions.map((promo) => (
                <div
                  key={promo.id}
                  className="p-4 rounded-xl bg-gradient-to-br from-viraly-orange/20 to-purple-500/20 border border-viraly-orange/30"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold">{promo.name}</p>
                      <p className="text-xs text-viraly-muted">
                        {promo.discount_type === "percentage" ? `${promo.discount_value}% off` : `$${promo.discount_value} off`}
                      </p>
                    </div>
                    {promo.priority === "high" && (
                      <span className="text-xs bg-red-500 px-2 py-1 rounded-full">Hot!</span>
                    )}
                  </div>
                  {promo.message && (
                    <p className="text-sm text-viraly-muted mb-3">{promo.message}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-viraly-muted">
                    <Clock className="w-3 h-3" />
                    <span>
                      Ends {new Date(promo.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Influencer Program Section */}
        {influencerStatus?.enabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-base p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-viraly-orange" />
                {influencerStatus?.is_influencer ? "Influencer Dashboard" : "Become an Influencer"}
              </h2>
              {influencerStatus?.is_influencer && (
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-viraly-orange to-purple-500 text-white text-xs font-bold">
                  INFLUENCER
                </span>
              )}
            </div>

            {influencerStatus?.is_influencer ? (
              /* Influencer Dashboard */
              <div className="space-y-6">
                {/* Earnings Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <p className="text-xs text-green-400">Total Earnings</p>
                    </div>
                    <p className="text-2xl font-bold">${influencerStatus.earnings?.total_earnings?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="w-4 h-4 text-blue-400" />
                      <p className="text-xs text-blue-400">Pending Balance</p>
                    </div>
                    <p className="text-2xl font-bold">${influencerStatus.earnings?.pending_balance?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowUpRight className="w-4 h-4 text-purple-400" />
                      <p className="text-xs text-purple-400">Paid Out</p>
                    </div>
                    <p className="text-2xl font-bold">${influencerStatus.earnings?.paid_out?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="w-4 h-4 text-viraly-orange" />
                      <p className="text-xs text-viraly-orange">Commission Rate</p>
                    </div>
                    <p className="text-2xl font-bold">{influencerStatus.settings?.commission_percent || 10}%</p>
                  </div>
                </div>

                {/* Payout Section */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="font-medium">Request Payout</p>
                    <p className="text-sm text-viraly-muted">
                      Min payout: ${influencerStatus.settings?.payout_threshold || 50}. 
                      {influencerStatus.payout_method?.method ? (
                        <span> Method: {influencerStatus.payout_method.method === "etransfer" ? "Interac e-Transfer" : "PayPal"}</span>
                      ) : (
                        <span className="text-yellow-400"> Set up your payout method first.</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => setShowPayoutModal(true)}
                      variant="outline"
                      className="text-sm"
                    >
                      {influencerStatus.payout_method ? "Update Method" : "Set Method"}
                    </Button>
                    <Button
                      onClick={handleRequestPayout}
                      className="btn-primary"
                      disabled={!influencerStatus.payout_method || (influencerStatus.earnings?.pending_balance || 0) < (influencerStatus.settings?.payout_threshold || 50)}
                    >
                      Request Payout
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-sm text-viraly-muted">
                    <span className="text-white font-medium">{influencerStatus.earnings?.eligible_transactions || 0}</span> transactions from referees, 
                    generating <span className="text-white font-medium">${influencerStatus.earnings?.eligible_revenue?.toFixed(2) || "0.00"}</span> in revenue.
                    You earn {influencerStatus.settings?.commission_percent || 10}% of all purchases your referees make!
                  </p>
                </div>
              </div>
            ) : (
              /* Progress to Influencer */
              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-gradient-to-r from-viraly-orange/10 to-purple-500/10 border border-viraly-orange/20">
                  <p className="text-sm mb-4">
                    Become an Influencer and earn <span className="text-viraly-orange font-bold">{influencerStatus?.settings?.commission_percent || 10}% commission</span> on 
                    every purchase your referees make — for life!
                  </p>
                  
                  {/* Requirements - Simplified */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-viraly-muted mb-1">Referees who purchased</p>
                      <p className="text-lg font-bold">
                        {influencerStatus?.eligibility?.current_referees_with_purchases || 0} / {influencerStatus?.settings?.min_referees || 10}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-viraly-muted mb-1">Qualifying purchases</p>
                      <p className="text-lg font-bold">
                        {Math.min(influencerStatus?.eligibility?.current_referees_with_purchases || 0, influencerStatus?.settings?.min_referees || 10)} paying referees
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-viraly-muted">Progress to Influencer</span>
                      <span className="text-viraly-orange">{(influencerStatus?.eligibility?.progress_percent || 0).toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${influencerStatus?.eligibility?.progress_percent || 0}%` }}
                        className="h-full bg-gradient-to-r from-viraly-orange to-purple-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="text-center text-sm text-viraly-muted">
                  <p>Get <span className="text-white font-semibold">10 people</span> to sign up using your code and purchase any plan.</p>
                  <p className="mt-2">Once qualified, you'll earn <span className="text-viraly-orange font-semibold">10% commission</span> on ALL their future purchases — paid monthly via PayPal!</p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Payout Method Modal */}
        {showPayoutModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-base p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-bold mb-4">Set Payout Method</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-viraly-muted mb-2 block">Payment Method</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPayoutMethod({...payoutMethod, method: "etransfer"})}
                      className={`p-3 rounded-lg border text-sm ${payoutMethod.method === "etransfer" ? "border-viraly-orange bg-viraly-orange/10" : "border-white/10"}`}
                    >
                      Interac e-Transfer
                      <p className="text-xs text-viraly-muted">(Canada only)</p>
                    </button>
                    <button
                      onClick={() => setPayoutMethod({...payoutMethod, method: "paypal"})}
                      className={`p-3 rounded-lg border text-sm ${payoutMethod.method === "paypal" ? "border-viraly-orange bg-viraly-orange/10" : "border-white/10"}`}
                    >
                      PayPal
                      <p className="text-xs text-viraly-muted">(International)</p>
                    </button>
                  </div>
                </div>

                {payoutMethod.method === "etransfer" && (
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">e-Transfer Email</label>
                    <Input
                      type="email"
                      placeholder="your-email@example.com"
                      value={payoutMethod.etransfer_email || ""}
                      onChange={(e) => setPayoutMethod({...payoutMethod, etransfer_email: e.target.value})}
                      className="input-base"
                    />
                  </div>
                )}

                {payoutMethod.method === "paypal" && (
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">PayPal Email</label>
                    <Input
                      type="email"
                      placeholder="your-paypal@example.com"
                      value={payoutMethod.paypal_email || ""}
                      onChange={(e) => setPayoutMethod({...payoutMethod, paypal_email: e.target.value})}
                      className="input-base"
                    />
                    <p className="text-xs text-yellow-400 mt-2">Note: PayPal may not be available for receiving payments in some countries.</p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-viraly-muted mb-2 block">Notes (optional)</label>
                  <Input
                    placeholder="Any special instructions..."
                    value={payoutMethod.notes || ""}
                    onChange={(e) => setPayoutMethod({...payoutMethod, notes: e.target.value})}
                    className="input-base"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowPayoutModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSavePayoutMethod} className="btn-primary flex-1">
                  Save Method
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-base p-6"
        >
          <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-viraly-orange" />
            How Rewards Work
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-viraly-orange/20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-viraly-orange" />
              </div>
              <h3 className="font-medium mb-2">Spend & Earn Tiers</h3>
              <p className="text-sm text-viraly-muted">
                Your purchases over the last 12 months determine your tier. Higher tiers = bigger discounts on every purchase.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="font-medium mb-2">Refer Friends</h3>
              <p className="text-sm text-viraly-muted">
                Share your code. When friends sign up and purchase, you both get rewards - free credits for you, discounts for them.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Percent className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="font-medium mb-2">Stack Discounts</h3>
              <p className="text-sm text-viraly-muted">
                Your tier discount + active promotions + coupons all stack together (up to {loyaltyStatus?.settings?.max_discount_percent || 50}% max).
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
